import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppProvider';
import { Download, AlertCircle, Database, Plug } from 'lucide-react';
import API_CONFIG from '../../config/api';
import  IntegrationRequestForm  from '../dependencies/RequestIntegration'


const BASE_URL = API_CONFIG.BASE_URL;

function DataTable({ tableName, data }) {
  const handleDownloadCSV = () => {
	if (!data || data.length === 0) return;
	
	// Get all unique keys from all rows
	const allKeys = [...new Set(data.flatMap(row => Object.keys(row)))];
	const headers = allKeys;
	const csvRows = [headers.join(',')];
	
	data.forEach(row => {
	  const csvRow = headers.map(header => {
		const value = row[header];
		// Handle null/undefined
		if (value === null || value === undefined) return '';
		// Handle booleans
		if (typeof value === 'boolean') return value;
		// Escape quotes and wrap in quotes if contains comma
		const stringValue = String(value);
		if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
		  return `"${stringValue.replace(/"/g, '""')}"`;
		}
		return stringValue;
	  });
	  csvRows.push(csvRow.join(','));
	});
	
	const csvContent = csvRows.join('\n');
	const blob = new Blob([csvContent], { type: 'text/csv' });
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `${tableName}-${new Date().toISOString().split('T')[0]}.csv`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(url);
  };

  if (!data || data.length === 0) {
	return (
	  <div className="flex items-center justify-center h-full">
		<p style={{ color: 'var(--color-muted)' }}>No data available</p>
	  </div>
	);
  }

  const columns = Object.keys(data[0]);

  return (
	<div className="h-full flex flex-col">
	  <div 
		className="flex items-center justify-between px-4 py-3 border-b"
		style={{ 
		  backgroundColor: 'color-mix(in srgb, var(--color-primary) 5%, var(--color-surface))',
		  borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
		}}
	  >
		<div className="flex items-center gap-2">
		  <Database className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
		  <span className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
			{data.length} rows
		  </span>
		</div>
		<button
		  onClick={handleDownloadCSV}
		  className="px-3 py-1.5 border transition-all duration-200 hover:shadow-md"
		  style={{
			backgroundColor: 'var(--color-surface)',
			borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)',
			color: 'var(--color-text)'
		  }}
		  onMouseEnter={(e) => {
			e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 5%, var(--color-surface))';
		  }}
		  onMouseLeave={(e) => {
			e.currentTarget.style.backgroundColor = 'var(--color-surface)';
		  }}
		  title="Download as CSV"
		>
		  <div className="flex items-center gap-2">
			<Download className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
			<span className="text-sm font-medium">Export CSV</span>
		  </div>
		</button>
	  </div>
	  
	  <div className="flex-1 overflow-auto">
		<table className="w-full">
		  <thead 
			className="sticky top-0"
			style={{ 
			  backgroundColor: 'color-mix(in srgb, var(--color-primary) 10%, var(--color-surface))',
			  borderBottom: '2px solid var(--color-primary)'
			}}
		  >
			<tr>
			  {columns.map((column) => (
				<th 
				  key={column}
				  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
				  style={{ color: 'var(--color-text)' }}
				>
				  {column.replace(/_/g, ' ')}
				</th>
			  ))}
			</tr>
		  </thead>
		  <tbody>
			{data.map((row, rowIndex) => (
			  <tr 
				key={rowIndex}
				style={{ 
				  borderBottom: `1px solid color-mix(in srgb, var(--color-muted) 30%, transparent)`,
				  backgroundColor: rowIndex % 2 === 0 ? 'var(--color-surface)' : 'color-mix(in srgb, var(--color-bg) 50%, var(--color-surface))'
				}}
			  >
				{columns.map((column) => {
				  const value = row[column];
				  let displayValue = value;
				  
				  // Format different data types
				  if (value === null || value === undefined) {
					displayValue = '';
				  } else if (typeof value === 'boolean') {
					displayValue = value ? '✓' : '✗';
				  } else if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
					// Likely a date
					try {
					  displayValue = new Date(value).toLocaleString();
					} catch {
					  displayValue = value;
					}
				  }
				  
				  return (
					<td 
					  key={column}
					  className="px-4 py-3 text-sm whitespace-nowrap"
					  style={{ color: 'var(--color-text)' }}
					>
					  {displayValue}
					</td>
				  );
				})}
			  </tr>
			))}
		  </tbody>
		</table>
	  </div>
	</div>
  );
}

export default function ExportDataFeature({ featureName }) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [exportData, setExportData] = useState(null);
	const [activeTab, setActiveTab] = useState(null);
	const { tenantInfos } = useApp();
	
	const [showForm, setShowForm] = useState(false);
	const connectors = [
		{ name: 'Google Sheets', logo: '/logos/google-sheets.svg' },
		{ name: 'SAP', logo: '/logos/google-sheets.svg' },
		{ name: 'Salesforce', logo: '/logos/salesforce.svg' },
		{ name: 'HubSpot', logo: '/logos/hubspot.svg' },
		{ name: 'Snowflake', logo: '/logos/snowflake.svg' },
		{ name: 'Custom', logo: '/logos/snowflake.svg' },
	  ];


	const fetchFeatureData = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		
		try {
			const tenant_id = tenantInfos.interfaceId;
			const access_token = tenantInfos.accessToken;
			const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/get_data/${featureName}`;
			const result = await fetch(api_url);
			
			if (result.status === 404) {
				setError({ statusCode: 404, detail: "Feature data not found" });
				return;
			}
			if (result.status === 401) {
				setError({ statusCode: 401, detail: "You are not authorized to access this feature.\nPlease contact your administrator" });
				return;
			}
			if (!result.ok) {
				setError({ statusCode: 500, detail: "Unexpected error happened" });
				return;
			}

			const response = await result.json();
			
			if (response.success && response.data) {
				// Data is an array with one object containing table names as keys
				const dataObject = response.data;
				setExportData(dataObject);
				
				// Set first table as active by default
				const tables = Object.keys(dataObject);
				const firstTable = tables[0];
				if (firstTable) {
					setActiveTab(firstTable);
				}
			
			setError(null);
		} else if (response.error) {
			setError({ statusCode: response.error.status_code, detail: response.error.detail || response.error.message });
		} else {
			setError({ statusCode: 422, detail: "Unexpected data structure" });
		}
		} catch (error) {
			console.error("Error fetching feature data:", error);
			setError({ statusCode: 500, detail: "Failed to fetch feature data" });
		} finally {
			setIsLoading(false);
		}
	}, [featureName, tenantInfos]);

  useEffect(() => {
	fetchFeatureData();
  }, [fetchFeatureData]);

  // Loading state
  if (isLoading) {
	return (
	  <div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
		  <div className="text-center">
			<div 
			  className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
			  style={{ borderColor: 'var(--color-primary)' }}
			/>
			<p style={{ color: 'var(--color-muted)' }}>Loading export data...</p>
		  </div>
		</div>
	  </div>
	);
  }

  // Error state
  if (error) {
	return (
	  <div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
		  <div 
			className="p-6 border"
			style={{ 
			  backgroundColor: 'color-mix(in srgb, red 10%, var(--color-surface))',
			  borderColor: 'color-mix(in srgb, red 30%, transparent)'
			}}
		  >
			<div className="flex items-start gap-3">
			  <AlertCircle className="h-6 w-6 flex-shrink-0" style={{ color: 'red' }} />
			  <div>
				<h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
				  Error {error.statusCode}
				</h3>
				<p style={{ color: 'var(--color-text)', whiteSpace: 'pre-line' }}>
				  {error.detail}
				</p>
			  </div>
			</div>
		  </div>
		</div>
	  </div>
	);
  }

  const tableNames = exportData ? Object.keys(exportData) : [];

  return (
	<div className="mx-auto px-6 py-8">
	  {/* Header */}
	  <div className="flex items-end justify-between mb-6">
        <div className="flex items-end gap-2">
          <Download className="h-8 w-8 mb-1" style={{ color: 'var(--color-primary)' }} />
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
            Export Data
          </h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-tl-xl rounded-br-xl border transition-all duration-150 hover:shadow-md"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)',
            color: 'var(--color-primary)',
          }}
        >
          <Plug className="h-4 w-4" />
          <span className="font-medium text-sm">Request Integration</span>
        </button>
      </div>

	  {/* Spreadsheet-style container */}
	  <div 
		className="border"
		style={{ 
		  backgroundColor: 'var(--color-surface)',
		  borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)',
		  height: '600px',
		  display: 'flex',
		  flexDirection: 'column'
		}}
	  >
{/* Tab bar - spreadsheet style */}
<div
  className="flex items-end px-2 pt-2"
  style={{
    backgroundColor: 'var(--color-bg)',
    borderBottom: '2px solid var(--color-primary)',
  }}
>
  {tableNames.map((tableName) => {
    const isActive = activeTab === tableName;

    return (
      <button
        key={tableName}
        onClick={() => setActiveTab(tableName)}
        className={`
          relative px-4 py-2 text-sm font-medium transition-all duration-200 
          border-t border-l border-r
          ${isActive ? 'z-10' : 'z-0'}
          rounded-tr-xl
          shadow-sm
        `}
        style={{
          backgroundColor: isActive
            ? 'var(--color-surface)'
            : 'color-mix(in srgb, var(--color-muted) 8%, var(--color-bg))',
          borderColor: isActive
            ? 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
            : 'transparent',
          color: isActive ? 'var(--color-primary)' : 'var(--color-muted)',
          fontWeight: isActive ? '600' : '500',
          bottom: isActive ? '0' : '-2px', // lifts active tab visually
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor =
              'color-mix(in srgb, var(--color-muted) 15%, var(--color-bg))';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor =
              'color-mix(in srgb, var(--color-muted) 8%, var(--color-bg))';
          }
        }}
      >
        {tableName}
      </button>
    );
  })}
</div>

		{/* Table content area */}
		<div className="flex-1 overflow-hidden">
		  {activeTab && exportData[activeTab] && (
			<DataTable 
			  tableName={activeTab}
			  data={exportData[activeTab]}
			/>
		  )}
		</div>
		{showForm && (
        <IntegrationRequestForm 
          connectors={connectors}
          onClose={() => setShowForm(false)}
        />
      )}
	  </div>
	</div>
  );
}